import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  OnChanges,
  SimpleChanges,
  HostBinding,
  ElementRef,
  Renderer2,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-accordion-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './accordion-panel.component.html',
  styleUrl: './accordion-panel.component.scss',
})
export class AccordionPanelComponent implements OnChanges, OnInit {
  @Input() title = '';
  @Input() expanded = false;
  @Input() disabled = false;
  /** Duration of the animation in ms */
  @Input() duration = 450;
  /** Unique identifier for aria-controls */
  @Input() panelId = '';
  /** Optional list of items to render internally (numeric id) */
  @Input() items: { id: number; name: string }[] | null = null;
  /** Active id for applying styles */
  @Input() activeId: number | null = null;
  /** Emits the numeric id of the selected item */
  /** Emits when a change of state is requested (expand/collapse) */
  /** Dynamic height */
  /** Host class to allow the selector :host.expanded + :host */
  @Output() itemSelect = new EventEmitter<number>();

  /** Emits when a change of state is requested (expand/collapse) */
  @Output() panelToggled = new EventEmitter<boolean>();

  // dynamic height
  panelHeight = signal(0);
  private rafId: number | null = null;

  // Host class to allow the selector :host.expanded + :host
  @HostBinding('class.expanded') get hostExpanded() {
    return this.expanded;
  }
  @HostBinding('class.accordion-host') hostBase = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expanded'] && this.expanded) {
      this.scheduleMeasure();
    }
    if (changes['items'] && this.expanded) {
      this.scheduleMeasure();
    }
    // Re-measure when active item changes because font-weight or wrapping can alter total scrollHeight
    if (changes['activeId'] && this.expanded) {
      this.scheduleMeasure();
    }
    if (changes['expanded']) {
      this.updateOwnMargin();
      this.updateNextSiblingMargin();
    }
  }

  onToggleClick() {
    if (this.disabled) return;
    this.panelToggled.emit(!this.expanded);
  }

  private measure() {
    const el = document.getElementById(this.panelId);
    if (!el) return;
    const h = el.scrollHeight;
    const slack = this.items && this.items.length ? 24 : 0;
    this.panelHeight.set(h + slack);
  }

  private scheduleMeasure() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.measure();
      this.rafId = null;
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.updateOwnMargin();
      this.updateNextSiblingMargin();
    });
  }

  private updateOwnMargin() {
    const host = this.el.nativeElement as HTMLElement;
    const prev = host.previousElementSibling as HTMLElement | null;
    const margin = prev ? (prev.classList.contains('expanded') ? '16px' : '8px') : '0px';
    this.renderer.setStyle(host, 'margin-top', margin);
  }

  private updateNextSiblingMargin() {
    const host = this.el.nativeElement as HTMLElement;
    const next = host.nextElementSibling as HTMLElement | null;
    if (!next || next.tagName.toLowerCase() !== 'app-accordion-panel') return;
    const margin = this.expanded ? '16px' : '8px';
    this.renderer.setStyle(next, 'margin-top', margin);
  }

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}
}
